import React, { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Camera, HelpCircle, CheckCircle2, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { id: 'homewizard_p1',           label: 'P1-meter (slimme meter dongle)' },
  { id: 'homewizard_kwh_meter',    label: 'kWh-meter (tussenmeter)' },
  { id: 'homewizard_energy_socket', label: 'Slimme stekker (HomeWizard)' },
  { id: 'smart_plug_generic',      label: 'Slimme stekker (Shelly/anders)' },
  { id: 'homey_hub',               label: 'Homey hub' },
  { id: 'goodwe_inverter',         label: 'Zonnepaneel-omvormer' },
  { id: 'ev_charger_generic',      label: 'EV-laadpaal' },
  { id: 'heat_pump_generic',       label: 'Warmtepomp' },
  { id: 'boiler_generic',          label: 'Boiler / cv-ketel' },
  { id: 'battery_generic',         label: 'Thuisbatterij' },
  { id: 'unknown',                 label: 'Ik weet het niet' },
];

/**
 * DeviceSetupWizard — "Powersure herkent uw setup".
 *
 * 4-step flow:
 *   1) Category select OR "Ik weet het niet"
 *   2) Optional text/photo metadata (NO binary upload — only filename)
 *   3) Show top-3 recognition candidates
 *   4) Confirmation + capability summary
 *
 * NO live actuation. NO external AI/OCR. Photo metadata is logged but
 * the file itself is NEVER sent to the server.
 */
export default function DeviceSetupWizard({ sessionId, onComplete, onCancel }) {
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState('');
  const [text, setText] = useState('');
  const [photoMeta, setPhotoMeta] = useState(null); // {filename, content_type}
  const [candidates, setCandidates] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const logEvent = useCallback(async (event, meta) => {
    try {
      await fetch(`${API}/track/funnel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event,
          session_id: sessionId || 'mp-setup-wizard',
          meta: meta || {},
        }),
      });
    } catch { /* no-op */ }
  }, [sessionId]);

  const onPhotoPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // ONLY capture metadata. Never send the binary.
    setPhotoMeta({ filename: f.name, content_type: f.type });
  };

  const runRecognize = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API}/devices/intelligence/recognize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text || undefined,
          declared_category: category && category !== 'unknown' ? category : undefined,
          photo_filename: photoMeta?.filename,
          photo_content_type: photoMeta?.content_type,
          session_id: sessionId,
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.detail || 'recognize_failed');
      setCandidates((j.candidates || []).slice(0, 3));
      logEvent('device_recognition_attempted', { had_photo: !!photoMeta, had_text: !!text });
      setStep(2);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [text, category, photoMeta, sessionId, logEvent]);

  const confirm = useCallback(async (cand) => {
    setChosen(cand);
    logEvent('device_setup_confirmed', { device_kind: cand?.device_kind, confidence: cand?.confidence });
    setDone(true);
    setStep(3);
    if (onComplete) onComplete(cand);
  }, [logEvent, onComplete]);

  return (
    <div
      className="rounded-2xl border border-emerald-400/30 p-5 space-y-4"
      style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
      data-testid="mp-device-setup-wizard"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-300/90 font-semibold">Setup</div>
          <h3 className="text-lg sm:text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
            Powersure herkent uw setup
          </h3>
          <p className="text-[12.5px] text-white/65 mt-1">
            Voeg uw apparaten toe in 4 korte stappen. We sturen nog niets aan — alleen herkennen en meten.
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            data-testid="mp-wizard-close"
            className="text-xs text-white/55 hover:text-white/80"
          >
            Sluiten
          </button>
        )}
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-1.5">
        {[0,1,2,3].map((s) => (
          <div
            key={s}
            data-testid={`mp-wizard-step-${s}`}
            className={`h-1 flex-1 rounded-full transition-colors ${
              s <= step ? 'bg-emerald-400' : 'bg-white/15'
            }`}
          />
        ))}
      </div>

      {/* Step 0: Category */}
      {step === 0 && (
        <div className="space-y-3" data-testid="mp-wizard-step-content-0">
          <p className="text-sm text-white/80">Kies een categorie:</p>
          <div className="grid grid-cols-1 gap-2 max-h-72 overflow-auto">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { setCategory(c.id); setStep(1); }}
                data-testid={`mp-wizard-cat-${c.id}`}
                className={`text-left px-3 py-2 rounded-lg border transition-colors text-sm ${
                  category === c.id
                    ? 'bg-emerald-500/15 text-emerald-200 border-emerald-400/40'
                    : 'bg-white/5 text-white/80 border-white/10 hover:bg-white/10'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 1: Text/Photo */}
      {step === 1 && (
        <div className="space-y-3" data-testid="mp-wizard-step-content-1">
          <p className="text-sm text-white/80">
            Geef merk/model of upload een foto van het typeplaatje.{' '}
            <span className="text-white/55">(beide optioneel)</span>
          </p>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="bijv. Shelly Plus Plug S of GoodWe GW5000-EH"
            maxLength={120}
            data-testid="mp-wizard-text-input"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-sm text-white placeholder-white/35 focus:outline-none focus:border-emerald-400/50"
          />
          <label
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/15 cursor-pointer hover:bg-white/10 transition-colors"
            data-testid="mp-wizard-photo-label"
          >
            <Camera className="w-4 h-4 text-emerald-300" />
            <span className="text-sm text-white/80">
              {photoMeta ? `Foto gekozen: ${photoMeta.filename}` : 'Foto toevoegen (optioneel)'}
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={onPhotoPick}
              className="hidden"
              data-testid="mp-wizard-photo-input"
            />
          </label>
          <p className="text-[11px] text-white/45">
            Wij slaan uw foto NIET op. Alleen de bestandsnaam wordt gelogd. Foto wordt NIET gebruikt voor herkenning.
          </p>
          {err && (
            <div className="text-xs text-amber-300 inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {err}
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => setStep(0)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/65 text-xs border border-white/15"
              data-testid="mp-wizard-back-1"
            >
              <ChevronLeft className="w-3 h-3" /> Terug
            </button>
            <button
              type="button"
              onClick={runRecognize}
              disabled={loading}
              data-testid="mp-wizard-recognize"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-semibold disabled:opacity-50"
            >
              {loading ? 'Herkennen…' : 'Herken apparaat'}
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Top-3 candidates */}
      {step === 2 && (
        <div className="space-y-3" data-testid="mp-wizard-step-content-2">
          <p className="text-sm text-white/80">Top-3 kandidaten — klopt dit?</p>
          {candidates.length === 0 && (
            <div className="text-xs text-white/55">Geen kandidaten gevonden. Kies handmatig.</div>
          )}
          <ul className="space-y-2">
            {candidates.map((c) => (
              <li
                key={c.device_kind}
                className="rounded-xl border border-white/10 bg-white/[0.04] p-3"
                data-testid={`mp-wizard-candidate-${c.device_kind}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="text-sm font-semibold text-white">
                    {c.vendor} — {c.device_kind.replace(/_/g, ' ')}
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-300">
                    {Math.round((c.confidence || 0) * 100)}%
                  </span>
                </div>
                {c.user_facing_blurb && (
                  <p className="text-[11.5px] text-white/65 leading-snug mb-1.5">{c.user_facing_blurb}</p>
                )}
                <div className="text-[11px] text-white/55 mb-2">
                  Koppelmethode: {(c.coupling_methods || []).join(', ') || 'handmatig'} ·
                  {' '}{c.control_capabilities?.length ? 'later mogelijk stuurbaar' : 'alleen meten'}
                </div>
                <button
                  type="button"
                  onClick={() => confirm(c)}
                  data-testid={`mp-wizard-confirm-${c.device_kind}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-[12px] border border-emerald-400/30"
                >
                  <CheckCircle2 className="w-3 h-3" /> Klopt dit
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setStep(1)}
            data-testid="mp-wizard-other-device"
            className="text-xs text-white/55 hover:text-white/80 inline-flex items-center gap-1"
          >
            <HelpCircle className="w-3 h-3" /> Ander apparaat — opnieuw
          </button>
        </div>
      )}

      {/* Step 3: Done */}
      {step === 3 && chosen && (
        <div className="space-y-2" data-testid="mp-wizard-step-content-3">
          <div className="inline-flex items-center gap-1 text-emerald-300 text-sm font-semibold">
            <CheckCircle2 className="w-4 h-4" /> Apparaat toegevoegd
          </div>
          <div className="text-sm text-white/80">
            <span className="text-white">{chosen.vendor}</span> — {chosen.device_kind.replace(/_/g, ' ')}
          </div>
          <p className="text-[12px] text-white/65 leading-snug">
            {chosen.control_capabilities?.length
              ? 'Dit apparaat zou later stuurbaar zijn na uw expliciete toestemming. Powersure schakelt nu nog niets.'
              : 'Dit apparaat wordt alleen gemeten. Geen sturing nodig of mogelijk.'}
          </p>
          {chosen.external_blocker && (
            <p className="text-[11px] text-amber-300">
              Externe stap nodig: {chosen.external_blocker}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
